import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import type {
  UpdateVisitorCustomFieldConfigStatusMutation,
  UpdateVisitorCustomFieldConfigStatusMutationVariables,
} from 'graphql-query-contracts';
import { updateVisitorCustomFieldConfigStatus } from 'graphql-queries';
import type { CustomFieldDetailModel } from './types';
import {
  NO_ORGANIZATION_KEY,
  runUpdateVisitorCustomFieldStatus,
} from './customFieldsTabModelUtils';

export function useCustomFieldStatusUpdate({
  organizationId,
}: {
  organizationId: string | null;
}) {
  const { t } = useTranslation();
  const [pendingDisableRow, setPendingDisableRow] = useState<CustomFieldDetailModel | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Synchronous guard against double-click races: setIsLoading(true) only
  // commits on the next render, so two clicks fired in the same tick would
  // both see isLoading === false and both kick off mutations. A ref flips
  // synchronously and closes that window.
  const inFlightRef = useRef(false);

  const [updateFieldStatus] = useMutation<
    UpdateVisitorCustomFieldConfigStatusMutation,
    UpdateVisitorCustomFieldConfigStatusMutationVariables
  >(updateVisitorCustomFieldConfigStatus, {
    errorPolicy: 'all',
    update(cache, { data }) {
      const updatedConfig = data?.updateVisitorCustomFieldConfigStatus;

      if (!updatedConfig) return;

      cache.modify({
        id: cache.identify(updatedConfig),
        fields: {
          isEnabled: () => updatedConfig.isEnabled,
          updatedAtDatetime: () => updatedConfig.updatedAtDatetime,
        },
      });
    },
  });

  const showToast = useCallback((msg: string) => window.Toast.show(msg), []);

  const executeStatusUpdate = useCallback(
    async (row: CustomFieldDetailModel, isEnabled: boolean): Promise<boolean> => {
      if (inFlightRef.current) return false;

      const orgId = row.organizationId || organizationId;

      if (!orgId) {
        showToast(t(NO_ORGANIZATION_KEY));

        return false;
      }

      inFlightRef.current = true;
      setIsLoading(true);

      try {
        return await runUpdateVisitorCustomFieldStatus({
          updateFieldStatus,
          showToast,
          t,
          orgId,
          id: row.id,
          isEnabled,
        });
      } finally {
        inFlightRef.current = false;
        setIsLoading(false);
      }
    },
    [organizationId, showToast, t, updateFieldStatus]
  );

  const handleToggleStatus = useCallback(
    (row: CustomFieldDetailModel): Promise<boolean> => {
      if (row.isEnabled) {
        setPendingDisableRow(row);

        return Promise.resolve(false);
      }

      return executeStatusUpdate(row, true);
    },
    [executeStatusUpdate]
  );

  // Dismiss the confirmation dialog before the mutation starts so the loading
  // indicator surfaces on the list (not the modal), per UX expectation.
  const handleDisableConfirm = useCallback(async (): Promise<void> => {
    const row = pendingDisableRow;

    if (!row) return;

    setPendingDisableRow(null);
    await executeStatusUpdate(row, false);
  }, [pendingDisableRow, executeStatusUpdate]);

  const handleDisableCancel = useCallback((): void => {
    setPendingDisableRow(null);
  }, []);

  return {
    handleToggleStatus,
    handleDisableConfirm,
    handleDisableCancel,
    pendingDisableRow,
    isLoading,
  };
}
